import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { Building2, Hash, Battery, Wifi, Clock, MapPin } from "lucide-react";
import { Modal } from "antd";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Dot,
  Legend,
} from "recharts";
import { Spin, Input } from "antd";
import { getMeterLocationList } from "../services/index";
import { useDistrict } from "../utils/DistrictContext";
import { MarkerClustererF } from "@react-google-maps/api";
import clusterIcon from "../assets/images/m1.png";
import { useTheme } from "../context/ThemeContext";
import { ConfigProvider, DatePicker, Progress, theme as antTheme } from "antd";
import { Radio } from "antd";
import { useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import dayjs from "dayjs";
import { get } from "../Libraries/Ajax/HttpService";
import moment from "moment";
import { SwapOutlined } from "@ant-design/icons";
const { Search } = Input;

const containerStyle = {
  width: "100%",
  height: "86vh",
  borderRadius: "12px",
  position: "relative",
};

const defaultCenter = { lat: 29.92, lng: -95.2 };

const MapTab = () => {
  const [meters, setMeters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredMeter, setHoveredMeter] = useState(null);
  const [activeSearchMeter, setActiveSearchMeter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [map, setMap] = useState(null);
  const { selectedDistrict } = useDistrict();
  const [filterType, setFilterType] = useState("active");
  const [colorFilter, setColorFilter] = useState("all");
  const { theme, colors } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMeterData, setSelectedMeterData] = useState(null);
  const [searchParams] = useSearchParams();
  const category = searchParams.get("category");
  const meterId = searchParams.get("meter_id");
  const timeRange = searchParams.get("time_range");
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  const selectedTime = searchParams.get("selected_time");
  const { RangePicker } = DatePicker;
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  });
  const today = dayjs().format("YYYY-MM-DD");
  const oneMonthAgo = dayjs().subtract(1, "month").format("YYYY-MM-DD"); // Create one month ago constant

  const [startDate, setStartDate] = useState(
  selectedTime ? dayjs(selectedTime).format("YYYY-MM-DD") : oneMonthAgo // Changed from 'today' to 'oneMonthAgo'
  );

  const [endDate, setEndDate] = useState(today);
  const [anomalyData, setAnomalyData] = useState([]);
  const [hardwareDetails, setHardwareDetails] = useState(null);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const safeRound = (val) => {
  if (val === null || val === undefined) return null;
  const cleaned = String(val).replace(/[$,GPH\s,]/g, "");
  const num = parseFloat(cleaned);
  return isNaN(num) ? null : Math.round(num);
};
  function InfoTile({ label, value, icon, variant = "default" }) {
    const { colors } = useTheme();
    const variantStyles = {
      default: {
        borderColor: `${colors.border}`,
        backgroundColor: `${colors.cardBg}0D`,
      },
      warning: { borderColor: `#eab30850`, backgroundColor: `#eab30810` },
      danger: { borderColor: `#ef444450`, backgroundColor: `#ef444410` },
    };

    return (
      <div
        className="rounded-xl border p-4 flex items-start gap-3"
        style={variantStyles[variant]}
      >
        <div
          className="mt-1"
          style={{
            color:
              variant === "danger"
                ? "#ef4444"
                : variant === "warning"
                  ? "#eab308"
                  : colors.primary,
          }}
        >
          {icon}
        </div>
        <div>
          <span
            className="text-xs font-semibold block mb-1"
            style={{ color: colors.textSecondary }}
          >
            {label}
          </span>
          <p className="text-sm font-bold" style={{ color: colors.text }}>
            {value || "N/A"}
          </p>
        </div>
      </div>
    );
  }
  // Add 'get' to your imports if not already there: import { get } from "../Libraries/Ajax/HttpService";

  useEffect(() => {
    if (!isModalOpen || !selectedMeterData) return;

    const fetchDetailedData = async () => {
      setIsModalLoading(true);
      setAnomalyData([]); // Clear old data
      try {
        // Use the standard 'get' helper for the specific anomaly API
        const response = await get("apis/anoamaly_detection", {
          params: {
            meter_id: selectedMeterData.meter_id,
            start_date: startDate,
            end_date: endDate,
            time_range: "Custom",
          },
        });

        const payload = response.data;
        const rootObject = Array.isArray(payload) ? payload[0] : payload || {};

        if (rootObject?.data) {
          const formatted = rootObject.data.map((item) => ({
            name: item.day,
            gallons: parseFloat(item.total_daily_flow) || 0, // Ensure it's a number
            threshold: safeRound(item.threshold_value),
            rolling: safeRound(item.upper_rolling_threshold),
            historical: safeRound(item.historical_guardrail_upper),
            isAnomaly: item.anomaly === 1,
            anomalyReason: item.anomaly_reason,
            suppressedReason: item.anomaly_suppressed_reason,
          }));
          setAnomalyData(formatted);
          setHardwareDetails(rootObject.meter_details?.[0] || null);
        }
      } catch (e) {
        console.error("Anomaly API Error:", e);
      } finally {
        setIsModalLoading(false);
      }
    };

    fetchDetailedData();
  }, [isModalOpen, selectedMeterData, startDate, endDate]);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
 const AnomalyTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      // Formatting logic for thresholds to match Detail view
      const formatVal = (val) =>
        val !== null && val !== undefined
          ? `${val.toLocaleString()} Gal`
          : "N/A";

      return (
        <div
          className="p-4 border rounded-lg shadow-lg min-w-[240px]"
          style={{
            backgroundColor: colors.background,
            borderColor: `${colors.primary}30`,
            boxShadow: `0 8px 32px rgba(0,0,0,0.3)`,
            backdropFilter: "blur(6px)",
          }}
        >
          {/* Date Header */}
          <p
            className="text-xs mb-3 font-bold border-b pb-2"
            style={{ color: colors.text, borderColor: colors.border }}
          >
            {label ? dayjs(label).format("MMM DD, YYYY") : "N/A"}
          </p>

          {/* Data Rows */}
          <div className="space-y-1.5">
            <div className="flex justify-between gap-4">
              <span className="text-[11px]" style={{ color: colors.textSecondary }}>
                Daily Flow:
              </span>
              <span className="text-[11px] font-bold" style={{ color: colors.primary }}>
                {Math.round(data.gallons).toLocaleString()} Gal
              </span>
            </div>

            <div className="flex justify-between gap-4">
              <span className="text-[11px]" style={{ color: colors.textSecondary }}>
                Threshold:
              </span>
              <span className="text-[11px] font-semibold" style={{ color: colors.text }}>
                {formatVal(data.threshold)}
              </span>
            </div>

            <div className="flex justify-between gap-4">
              <span className="text-[11px]" style={{ color: colors.textSecondary }}>
                Rolling:
              </span>
              <span className="text-[11px] font-semibold" style={{ color: colors.text }}>
                {formatVal(data.rolling)}
              </span>
            </div>

            <div className="flex justify-between gap-4">
              <span className="text-[11px]" style={{ color: colors.textSecondary }}>
                Historical:
              </span>
              <span className="text-[11px] font-semibold" style={{ color: colors.text }}>
                {formatVal(data.historical)}
              </span>
            </div>
          </div>

          {/* Suppression Section */}
          {data.suppressedReason && (
            <div 
              className="mt-3 pt-2 border-t text-[11px] leading-tight" 
              style={{ color: "#94a3b8", borderColor: colors.border }}
            >
              <span className="font-bold">Suppressed:</span> {data.suppressedReason}
            </div>
          )}

          {/* Anomaly Section */}
          {data.isAnomaly && !data.suppressedReason && (
            <div 
              className="mt-3 pt-2 border-t border-red-900/30 text-[11px] leading-tight" 
              style={{ color: "#f87171" }}
            >
              <div className="flex items-center gap-1 mb-1">
                <span className="font-bold uppercase tracking-wider text-[10px]">
                  ⚠️ Anomaly Detected
                </span>
              </div>
              <span className="font-bold">Reason:</span> {data.anomalyReason || "Threshold exceeded"}
            </div>
          )}
        </div>
      );
    }
    return null;
  };
  useEffect(() => {
    const fetchMeterLocations = async () => {
      try {
        // 2. Call the new function
        const response = await getMeterLocationList(filterType);

        // Based on your JSON structure, data is in response.data.meters
        const allData = response?.data?.meters || [];

        // Deduplicate
        const uniqueData = allData.filter(
          (meter, index, self) =>
            index ===
            self.findIndex(
              (m) =>
                m.location_id === meter.location_id &&
                m.meter_id === meter.meter_id,
            ),
        );

        setMeters(uniqueData);
      } catch (e) {
        console.error("Failed to fetch map data", e);
      } finally {
        setLoading(false);
      }
    };
    fetchMeterLocations();
  }, [selectedDistrict, filterType]);
  const getMeterColor = (status) => {
    switch (status?.toLowerCase()) {
      case "green":
        return "#00e5a8"; // Green
      case "red":
        return "#ff4d4f"; // Red
      case "orange":
        return "#ffa940"; // Orange
      default:
        return "#00e5a8"; // Fallback
    }
  };
  useEffect(() => {
    if (!map) return;

    // 🎯 District-specific centers
    const districtCenters = {
      FB151: { lat: 29.753417, lng: -95.861396 },
      GPMD: { lat: 29.92312, lng: -95.20392 },
    };

    const districtCenter = districtCenters[selectedDistrict];

    // ✅ If district has fixed center
    if (districtCenter) {
      map.panTo(districtCenter);
      map.setZoom(15);
      return;
    }

    // 🌍 Default: fit all markers
    if (meters.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();

      meters.forEach((m) => {
        if (
          m.location_latitude &&
          m.location_longitude &&
          m.location_latitude !== "0" &&
          m.location_longitude !== "0"
        ) {
          bounds.extend({
            lat: parseFloat(m.location_latitude),
            lng: parseFloat(m.location_longitude),
          });
        }
      });

      map.fitBounds(bounds);
    }
  }, [map, meters, selectedDistrict]);

  const filteredMeters = useMemo(() => {
    let result = meters;

    // 🔍 Search filter
    if (searchQuery) {
      result = result.filter(
        (m) =>
          m.meter_id.toLowerCase().includes(searchQuery.toLowerCase()) ||
          m.location_address?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    // 🎨 Color filter
    if (colorFilter !== "all") {
      result = result.filter(
        (m) => m.operational?.toLowerCase() === colorFilter,
      );
    }

    return result;
  }, [meters, searchQuery, colorFilter]);

  const adjustedMeters = useMemo(() => {
    const coordinateGroups = {};

    return filteredMeters.map((meter) => {
      const lat = parseFloat(meter.location_latitude);
      const lng = parseFloat(meter.location_longitude);

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        return {
          ...meter,
          adjustedLat: lat,
          adjustedLng: lng,
        };
      }

      const key = `${lat.toFixed(6)}_${lng.toFixed(6)}`;

      if (!coordinateGroups[key]) {
        coordinateGroups[key] = 0;
      }

      const index = coordinateGroups[key];
      coordinateGroups[key] += 1;

      // small circular spread for meters at same location
      const angle = (index * Math.PI) / 4;
      const offset = 0.00003 * Math.ceil(index / 2);

      return {
        ...meter,
        adjustedLat: lat + Math.cos(angle) * offset,
        adjustedLng: lng + Math.sin(angle) * offset,
      };
    });
  }, [filteredMeters]);

  const onSearch = (value) => {
    setSearchQuery(value);
    const found = meters.find(
      (m) =>
        m.meter_id.toLowerCase() === value.toLowerCase() ||
        m.location_address?.toLowerCase().includes(value.toLowerCase()),
    );

    if (found && map) {
      setActiveSearchMeter(found);
      map.panTo({
        lat: parseFloat(found.location_latitude),
        lng: parseFloat(found.location_longitude),
      });
      map.setZoom(17);
    }
  };

  const onMapLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  if (!isLoaded || loading) {
    return (
      <div className="h-[600px] flex items-center justify-center">
        <Spin size="large" tip="Loading Pins..." />
      </div>
    );
  }

  const selectedDisplayMeter = activeSearchMeter || hoveredMeter;
  const disabledDate = (current) => {
    // Can not select days after today
    return current && current > moment().endOf("day");
  };
  const handleRangeChange = (dates) => {
    if (!dates) return;

    const [start, end] = dates;

    setStartDate(start.format("YYYY-MM-DD"));
    setEndDate(end.format("YYYY-MM-DD"));
  };

  const RenderCustomLegend = () => {
    const legendItems = [
      { label: "Daily Flow", dash: "none", width: 4.5 },
      { label: "Threshold", dash: "12 6", width: 2.5 },
      { label: "Rolling", dash: "1 10", width: 4 },
      { label: "Historical", dash: "15 4 3 4", width: 1.5 },
    ];

    return (
      <div className="flex flex-wrap justify-center gap-3 mb-6">
        {legendItems.map((item, index) => (
          <div key={index} className="flex items-center gap-3">
            <svg width="45" height="12" className="inline-block">
              <line
                x1="0"
                y1="6"
                x2="45"
                y2="6"
                stroke={colors.text} // ✅ THEME COLOR HERE
                strokeWidth={item.width}
                strokeDasharray={item.dash}
                strokeLinecap="round"
              />
            </svg>

            <span
              className="text-[12px] font-bold tracking-wide"
              style={{ color: colors.text }}
            >
              {item.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ position: "relative", width: "100%" }}>
      {/* TOP CENTER: SEARCH BAR ONLY */}
      <div
        style={{
          position: "absolute",
          top: "15px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10,
          width: "80%",
          maxWidth: "400px",
        }}
      >
        <Search
          placeholder="Search Meter ID or Address..."
          allowClear
          enterButton="Search"
          size="large"
          onSearch={onSearch}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (!e.target.value) setActiveSearchMeter(null);
          }}
          style={{ boxShadow: "0 4px 12px rgba(0,0,0,0.2)" }}
        />
      </div>

      {/* BOTTOM RIGHT: TOGGLE (Near Zoom Icons) */}
      <div
        style={{
          position: "absolute",
          bottom: "100px",
          right: "12px",
          zIndex: 10,
          background: colors.background,
          backdropFilter: "blur(10px)",
          borderRadius: "12px",
          padding: "12px",
          boxShadow: "0 8px 20px rgba(0,0,0,0.4)",
          width: "150px",
          border: `1px solid ${colors.border}`,
        }}
      >
        {/* TITLE */}
        <div
          style={{
            color: colors.text,
            fontSize: "13px",
            marginBottom: "8px",
            opacity: 0.8,
            fontWeight: "600",
          }}
        >
          Filters
        </div>

        {/* STATUS FILTER */}
        <div style={{ marginBottom: "10px" }}>
          <div
            style={{
              fontSize: "11px",
              color: colors.textSecondary,
              marginBottom: "5px",
            }}
          >
            Status
          </div>

          <div style={{ display: "flex", gap: "6px" }}>
            <button
              onClick={() => setFilterType("active")}
              style={{
                flex: 1,
                padding: "6px",
                borderRadius: "6px",
                border: `1px solid ${colors.border}`,
                background:
                  filterType === "active" ? colors.gradient : colors.cardBg,
                color: colors.text,
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: "500",
              }}
            >
              Active
            </button>

            <button
              onClick={() => setFilterType("all")}
              style={{
                flex: 1,
                padding: "6px",
                borderRadius: "6px",
                border: `1px solid ${colors.border}`,
                background:
                  filterType === "all" ? colors.gradient : colors.cardBg,
                color: colors.text,
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: "500",
              }}
            >
              All
            </button>
          </div>
        </div>

        {/* COLOR FILTER */}
        <div>
          <div
            style={{
              fontSize: "11px",
              color: colors.textSecondary,
              marginBottom: "5px",
            }}
          >
            Operational Status
          </div>

          {[
            { label: "All", value: "all", color: "#ccc" },
            { label: "Green", value: "green", color: "#00e5a8" },
            { label: "Red", value: "red", color: "#ff4d4f" },
            { label: "Orange", value: "orange", color: "#ffa940" },
          ].map((item) => (
            <div
              key={item.value}
              onClick={() => setColorFilter(item.value)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "6px",
                borderRadius: "6px",
                marginBottom: "5px",
                background:
                  colorFilter === item.value ? colors.hoverBg : "transparent",
                cursor: "pointer",
                transition: "0.2s",
                border:
                  colorFilter === item.value
                    ? `1px solid ${colors.primary}`
                    : "1px solid transparent",
              }}
            >
              {/* COLOR DOT */}
              <div
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  background: item.color,
                }}
              />

              {/* TEXT */}
              <span
                style={{
                  color: colors.text,
                  fontSize: "12px",
                  fontWeight: "500",
                }}
              >
                {item.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={defaultCenter}
        zoom={15}
        onLoad={onMapLoad}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          gestureHandling: "greedy",
        }}
        onClick={() => {
          setActiveSearchMeter(null);
          setHoveredMeter(null);
        }}
      >
        <MarkerClustererF
          options={{
            zoomOnClick: false,
            maxZoom: 18, // after this, clusters break into markers
            styles: [
              {
                url: clusterIcon,
                height: 53,
                width: 53,
                textColor: "#ffffff",
                textSize: 10,
              },
            ],
          }}
          onClick={(cluster) => {
            if (!map) return;

            const currentZoom = map.getZoom() || 15;
            const center = cluster.getCenter();

            if (center) {
              map.panTo(center);
            }

            // step-by-step zoom
            if (currentZoom < 16) {
              map.setZoom(16); // 1st click
            } else if (currentZoom < 18) {
              map.setZoom(18); // 2nd click
            } else if (currentZoom < 20) {
              map.setZoom(20); // 3rd click
            } else {
              map.setZoom(21); // final zoom to show separated meters
            }
          }}
        >
          {(clusterer) =>
            adjustedMeters.map((meter) => {
              const meterColor = getMeterColor(meter.operational);
              const position = {
                lat: meter.adjustedLat,
                lng: meter.adjustedLng,
              };

              if (isNaN(position.lat) || isNaN(position.lng)) return null;

              return (
                <Marker
                  key={`${meter.location_id}-${meter.meter_id}`}
                  position={position}
                  clusterer={clusterer}
                  icon={{
                    url:
                      "data:image/svg+xml;base64," +
                      btoa(`
                      <svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="32" cy="32" r="31" fill="white" />
                        <circle cx="32" cy="32" r="27" fill="#0a1628" />
                        <path d="M18 40 A 18 18 0 1 1 46 40" fill="none" stroke="${meterColor}" stroke-width="5" stroke-linecap="round" />
                        <line x1="32" y1="32" x2="44" y2="20" stroke="${meterColor}" stroke-width="7" stroke-linecap="round" />
                        <circle cx="32" cy="32" r="5" fill="white" />
                      </svg>
                    `),
                    scaledSize: new window.google.maps.Size(40, 40),
                    anchor: new window.google.maps.Point(20, 20),
                  }}
                  onMouseOver={() => {
                    if (!activeSearchMeter) setHoveredMeter(meter);
                  }}
                  onMouseOut={() => {
                    if (!activeSearchMeter) setHoveredMeter(null);
                  }}
                  onClick={() => {
                    setSelectedMeterData(meter);
                    setIsModalOpen(true);
                    map.panTo(position);
                    map.setZoom(20);
                  }}
                />
              );
            })
          }
        </MarkerClustererF>
      </GoogleMap>

      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={1300}
        centered
        styles={{
          content: {
            backgroundColor: colors.background,
            border: `1px solid ${colors.border}`,
            padding: 0,
          },
        }}
      >
        {selectedMeterData && (
          <div className="flex flex-col">
            {/* HEADER WITH DATEPICKER */}
            <div
              className="p-6 border-b flex items-center justify-between flex-wrap gap-4"
              style={{ borderColor: colors.border }}
            >
              <h2 style={{ color: colors.text, margin: 0 }}>
                Meter {selectedMeterData.meter_id} Analysis
              </h2>
              <ConfigProvider
                theme={{
                  algorithm:
                    theme === "white"
                      ? antTheme.defaultAlgorithm
                      : antTheme.darkAlgorithm,
                }}
              >
                <RangePicker
                  value={[dayjs(startDate), dayjs(endDate)]}
                  onChange={handleRangeChange}
                  style={{
                    backgroundColor: colors.cardBg,
                    borderColor: colors.border,
                  }}
                />
              </ConfigProvider>
            </div>

            <div className="p-6 grid grid-cols-12 gap-6">
              {/* LEFT: GRAPH AREA */}
              <div
                className="col-span-12 lg:col-span-8 border rounded-xl p-4"
                style={{
                  backgroundColor: colors.cardBg,
                  borderColor: colors.border,
                }}
              >
                {isModalLoading ? (
                  <Spin tip="Fetching Anomaly Data..." />
                ) : (
                  <div className="h-[500px]">
                    <RenderCustomLegend />
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={anomalyData}
                        margin={{ top: 10, right: 30, left: 20, bottom: 20 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke={colors.border}
                          vertical={false}
                        />
                        <XAxis
                          dataKey="name"
                          stroke={colors.textSecondary}
                          fontSize={10}
                          tickFormatter={(str) =>
                            str ? dayjs(str).format("MMM DD") : ""
                          }
                        />
                        <YAxis
                          stroke={colors.textSecondary}
                          fontSize={10}
                          tickFormatter={(v) => v.toLocaleString()}
                        />
                        <Tooltip content={<AnomalyTooltip />} />

                        {/* Threshold Lines */}
                        <Line
                          type="monotone"
                          dataKey="threshold"
                          stroke={colors.textSecondary}
                          strokeDasharray="5 5"
                          dot={false}
                          connectNulls
                          opacity={0.4}
                        />
                        <Line
                          type="monotone"
                          dataKey="rolling"
                          stroke={colors.textSecondary}
                          strokeDasharray="1 4"
                          dot={false}
                          connectNulls
                          opacity={0.4}
                        />

                        {/* Main Flow Line */}
                        <Line
                          type="monotone"
                          dataKey="gallons" // This MUST match the key in useEffect
                          stroke={colors.primary}
                          strokeWidth={3}
                          dot={(props) => {
                            const { cx, cy, payload } = props;
                            if (
                              payload.isAnomaly &&
                              !payload.suppressedReason
                            ) {
                              return (
                                <Dot
                                  key={props.key}
                                  cx={cx}
                                  cy={cy}
                                  r={5}
                                  fill="#ef4444"
                                  stroke="#fff"
                                  strokeWidth={2}
                                />
                              );
                            }
                            return (
                              <Dot
                                key={props.key}
                                cx={cx}
                                cy={cy}
                                r={2}
                                fill={colors.primary}
                              />
                            );
                          }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* RIGHT: INFO PANEL */}
              <div className="col-span-12 lg:col-span-4 space-y-3 overflow-y-auto max-h-[600px]">
                <InfoTile
                  label="Account"
                  value={
                    selectedMeterData.meter_id
                    
                  }
                  icon={<Building2 size={18} />}
                />

                <div className="grid grid-cols-2 gap-2">
                  <InfoTile
                    label="Battery"
                    value={hardwareDetails?.battery_level}
                    icon={<Battery size={16} />}
                  />
                  <InfoTile
                    label="Signal"
                    value={hardwareDetails?.signal_strength}
                    icon={<Wifi size={16} />}
                  />
                </div>

                <div
                  className="p-4 rounded-xl border space-y-3"
                  style={{
                    backgroundColor: colors.cardBg,
                    borderColor: colors.border,
                  }}
                >
                  <p className="text-[10px] uppercase font-bold text-slate-400 flex items-center gap-2">
                    <Clock size={14} /> System Logs
                  </p>
                  <div className="space-y-2">
                    <div>
                      <span className="text-[10px] text-slate-500 block">
                        LAST COMMUNICATION
                      </span>
                      <p className="text-xs text-white">
                        {hardwareDetails?.last_comm_time || "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="text-[10px] text-slate-500 block">
                        LATEST FLOW RECORDED
                      </span>
                      <p className="text-xs text-white">
                        {hardwareDetails?.latest_flow_time || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                <InfoTile
                  label="Meter S/N"
                  value={hardwareDetails?.meter_sn}
                  icon={<Hash size={18} />}
                />
                <InfoTile
                  label="Address"
                  value={selectedMeterData.location_address}
                  icon={<MapPin size={18} />}
                />
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default MapTab;
